/**
 * MONSTER TOWN — THE BUILDING KIT. Content drop from the BUILD crew.
 *
 * Everything a kid needs to build a house on day one, and every piece is FREE
 * FOREVER (cost 0): walls, a door, a window, roofs, floors, paths, fences and
 * steps. The brief is explicit that a kid can never be stuck unable to build.
 *
 * ===================== LAW #5 LIVES IN THIS FILE =====================
 * "Kit pieces auto-snap: walls join corners, roofs fit the walls beneath,
 *  fences link, paths connect. Kids cannot build something ugly by accident."
 *
 * The mechanism, used by every piece below:
 *   - the def declares `connect: { group, style }`;
 *   - `buildArt` receives `ctx.connect`, a 4-bit N/E/S/W mask of which
 *     neighbours share that group;
 *   - the art is BUILT FROM THE MASK, so a wall tile with a neighbour to the
 *     east grows an arm that reaches the east edge, and its neighbour grows one
 *     back. There is no seam to get wrong because there is no seam: both pieces
 *     meet exactly at the tile boundary, every time, at every rotation.
 *   - the build system rebuilds art on every occupy/release, so the mask is
 *     never stale.
 *
 * A ROOF fits the walls beneath it the same way: every side of a roof tile that
 * has no roof neighbour slopes down and grows an eave; every side that does
 * stays full height. One tile is a little pyramid, a row is a ridge, a block is
 * a hipped roof — automatically, with no choice for a kid to get wrong.
 * =====================================================================
 */

import * as THREE from 'three'
import { FOOT_1x1, FOOT_WALL, MASK, rotateMask, type Rot } from '../core/grid'
import { definePlaceable, type ArtContext } from '../core/placeable'
import { mix, type PaletteName } from '../core/palette'
import type { PlaceableId } from '../core/types'
import { boxOn, group, inked, toon } from '../render/kit'

// ---------------------------------------------------------------------------
// shared helpers
// ---------------------------------------------------------------------------

/**
 * The connect mask rotated into the piece's LOCAL frame. Art is authored as if
 * the piece were at rot 0; the renderer applies the yaw. `rot 1` sends local +X
 * (east) to world +Z (south), so undoing it is a rotation of `4 - rot`.
 */
function localMask(ctx: ArtContext): number {
  return rotateMask(ctx.connect, (((4 - ctx.rot) % 4) as Rot))
}

interface Sides {
  n: boolean
  e: boolean
  s: boolean
  w: boolean
  any: boolean
}

function sidesOf(ctx: ArtContext): Sides {
  const m = localMask(ctx)
  const n = (m & MASK.N) !== 0
  const e = (m & MASK.E) !== 0
  const s = (m & MASK.S) !== 0
  const w = (m & MASK.W) !== 0
  return { n, e, s, w, any: n || e || s || w }
}

/** Darker relative of a palette colour — trims, plinths, eaves, mortar. */
function darker(c: PaletteName, amount = 0.22): number {
  return mix(c, 'ink', amount)
}

/** Lighter relative — highlights along a ridge or a step nose. */
function lighter(c: PaletteName, amount = 0.16): number {
  return mix(c, 'flowerWhite', amount)
}

// ---------------------------------------------------------------------------
// WALL — the auto-cornering piece the whole law rests on
// ---------------------------------------------------------------------------

const WALL_TINTS: readonly PaletteName[] = [
  'wallCream',
  'wallMint',
  'wallBlue',
  'wallPink',
  'wallLilac',
]

/**
 * A wall tile is drawn as one or two ARMS. Each arm runs from the middle of the
 * tile out to the tile edge on every side that has a wall neighbour, so two
 * neighbouring tiles always meet flush, and a corner is simply "an X arm and a
 * Z arm in the same tile".
 */
function wallArms(
  g: THREE.Group,
  ctx: ArtContext,
  body: PaletteName,
  height: number,
  thickness: number,
): void {
  const t = ctx.grid.tileSize
  const th = thickness
  const capTh = th * 1.2
  const sides = sidesOf(ctx)
  const hasX = sides.e || sides.w
  const hasZ = sides.n || sides.s
  const capH = height * 0.09
  const cap = darker(body, 0.26)
  // Arms are BUTTED, never overlapped: a corner used to draw a slightly thinner
  // Z arm straight through the X arm, so the two outer faces sat 2% apart and
  // the eye read a vertical light/dark seam down every join. Both arms now use
  // the SAME thickness and the Z arm stops at the X arm's face, which puts the
  // outer faces exactly coplanar — there is no seam because there is no
  // overlap. The hair of overlap below only stops a sub-pixel crack.
  const eps = th * 0.01

  if (hasX || !hasZ) {
    const x0 = sides.w ? -t / 2 : -th / 2
    const x1 = sides.e ? t / 2 : th / 2
    // Freestanding (no neighbours at all): a full-width panel, so a lone wall
    // still reads as a wall rather than a post.
    const w = hasX ? x1 - x0 : t
    const cx = hasX ? (x0 + x1) / 2 : 0
    g.add(boxOn(w, height - capH, th, { color: body, x: cx, name: 'wall' }))
    g.add(boxOn(w, capH, capTh, { color: cap, x: cx, y: height - capH, name: 'cap' }))
  }
  if (hasZ) {
    /** One Z arm: body spans [za,zb], its cap spans [ca,cb]. */
    const addZ = (za: number, zb: number, ca: number, cb: number): void => {
      if (zb - za > 0.002) {
        g.add(boxOn(th, height - capH, zb - za, { color: body, z: (za + zb) / 2, name: 'wall' }))
      }
      if (cb - ca > 0.002) {
        g.add(
          boxOn(capTh, capH, cb - ca, { color: cap, y: height - capH, z: (ca + cb) / 2, name: 'cap' }),
        )
      }
    }
    if (!hasX) {
      // A straight N/S run: one arm across the whole tile, nothing to butt into.
      const za = sides.n ? -t / 2 : -th / 2
      const zb = sides.s ? t / 2 : th / 2
      addZ(za, zb, za, zb)
    } else {
      // A corner. The X arm already fills |z| <= th/2 and its cap |z| <= capTh/2,
      // so each Z arm only fills what is left beyond them.
      if (sides.n) addZ(-t / 2, -th / 2 + eps, -t / 2, -capTh / 2)
      if (sides.s) addZ(th / 2 - eps, t / 2, capTh / 2, t / 2)
    }
  }
}

export const wallPlaster = definePlaceable<{ knocks: number }>({
  id: 'wall_plaster' as PlaceableId,
  devName: 'Plaster wall',
  category: 'wall',
  scale: 'both',
  footprint: FOOT_WALL,
  cost: 0,
  stackable: true,
  walkable: false,
  connect: { group: 'wall', style: 'wall' },
  tints: WALL_TINTS,
  tags: ['starter', 'shell'],
  initialState: () => ({ knocks: 0 }),

  buildArt: (ctx) => {
    const g = group('wall_plaster')
    const body = ctx.tint ?? 'wallCream'
    const H = ctx.grid.levelHeight * 2
    wallArms(g, ctx, body, H, ctx.grid.tileSize * 0.34)
    return g
  },

  // LAW #1: knock on the wall. It thumps, dust falls, and every fourth knock
  // somebody knocks back from the other side.
  interact: (ctx) => {
    const knocks = ctx.state.knocks + 1
    const answered = knocks % 4 === 0
    return {
      feedback: [
        { kind: 'anim', clip: answered ? 'shudder' : 'wobble', seconds: 0.35, target: 'wall' },
        { kind: 'particles', effect: answered ? 'stars' : 'dustPuff', count: answered ? 7 : 3 },
        { kind: 'sound', cue: answered ? 'knock_answer' : 'knock_wood' },
        { kind: 'state', patch: { knocks } },
      ],
      cooldownMs: 220,
    }
  },
})

// ---------------------------------------------------------------------------
// DOOR + WINDOW — same connect group as the wall, so walls run right into them
// ---------------------------------------------------------------------------

/** Doors and windows are drawn along one axis; the wall run picks which. */
function alignInner(ctx: ArtContext): THREE.Group {
  const inner = group('inner')
  const sides = sidesOf(ctx)
  const hasZ = sides.n || sides.s
  const hasX = sides.e || sides.w
  if (hasZ && !hasX) inner.rotation.y = Math.PI / 2
  return inner
}

export const doorWood = definePlaceable<{ open: boolean; swings: number }>({
  id: 'door_wood' as PlaceableId,
  devName: 'Front door',
  category: 'door',
  scale: 'both',
  footprint: FOOT_WALL,
  cost: 0,
  stackable: true,
  walkable: true,
  connect: { group: 'wall', style: 'wall' },
  tints: ['doorWood', 'roofTeal', 'roofRed', 'roofPurple'],
  tags: ['starter', 'shell'],
  initialState: () => ({ open: false, swings: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const H = ctx.grid.levelHeight * 2
    const th = t * 0.34
    const g = group('door_wood')
    const inner = alignInner(ctx)
    const frame = 'wallCream'
    const leafColor = ctx.tint ?? 'doorWood'

    inner.add(boxOn(t * 0.24, H, th, { color: frame, x: -t * 0.38, name: 'jamb' }))
    inner.add(boxOn(t * 0.24, H, th, { color: frame, x: t * 0.38, name: 'jamb' }))
    inner.add(boxOn(t * 0.76, H * 0.2, th, { color: frame, y: H * 0.8, name: 'lintel' }))

    const leaf = boxOn(t * 0.5, H * 0.72, th * 0.5, { color: leafColor, name: 'leaf' })
    inner.add(inked(leaf))
    inner.add(
      boxOn(t * 0.08, t * 0.08, th * 0.5, {
        color: darker(leafColor, 0.4),
        x: t * 0.17,
        y: H * 0.36,
        z: th * 0.3,
        name: 'knob',
      }),
    )
    g.add(inner)
    return g
  },

  // LAW #1: it opens and closes, and the monster steps through.
  interact: (ctx) => {
    const open = !ctx.state.open
    const swings = ctx.state.swings + 1
    return {
      feedback: [
        { kind: 'anim', clip: open ? 'doorOpen' : 'doorClose', seconds: 0.5, target: 'leaf' },
        { kind: 'toggle', on: open, target: 'leaf' },
        { kind: 'monster', action: open ? 'peek' : 'nod', seconds: 0.9 },
        { kind: 'particles', effect: open ? 'sparkle' : 'dustPuff', count: 4 },
        { kind: 'sound', cue: open ? 'door_open' : 'door_close' },
        { kind: 'state', patch: { open, swings } },
      ],
      cooldownMs: 260,
    }
  },
})

export const windowSquare = definePlaceable<{ curtains: boolean; peeks: number }>({
  id: 'window_square' as PlaceableId,
  devName: 'Cottage window',
  category: 'window',
  scale: 'both',
  footprint: FOOT_WALL,
  cost: 0,
  stackable: true,
  walkable: false,
  connect: { group: 'wall', style: 'wall' },
  tints: ['wallCream', 'wallMint', 'wallBlue', 'wallPink'],
  tags: ['starter', 'shell'],
  initialState: () => ({ curtains: true, peeks: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const H = ctx.grid.levelHeight * 2
    const th = t * 0.34
    const g = group('window_square')
    const inner = alignInner(ctx)
    const body = ctx.tint ?? 'wallCream'

    inner.add(boxOn(t, H * 0.38, th, { color: body, name: 'sill' }))
    inner.add(boxOn(t, H * 0.2, th, { color: body, y: H * 0.8, name: 'header' }))
    inner.add(boxOn(t * 0.2, H * 0.42, th, { color: body, x: -t * 0.4, y: H * 0.38, name: 'jamb' }))
    inner.add(boxOn(t * 0.2, H * 0.42, th, { color: body, x: t * 0.4, y: H * 0.38, name: 'jamb' }))
    inner.add(
      boxOn(t * 0.62, H * 0.42, th * 0.55, { color: 'glass', y: H * 0.38, name: 'glass' }),
    )
    inner.add(
      boxOn(t * 0.62, H * 0.05, th * 0.75, {
        color: darker(body, 0.3),
        y: H * 0.57,
        name: 'muntin',
      }),
    )
    g.add(inner)
    return g
  },

  // LAW #1: pull the curtains. Sunlight or cosy dark, and somebody waves back.
  interact: (ctx) => {
    const curtains = !ctx.state.curtains
    const peeks = ctx.state.peeks + 1
    return {
      feedback: [
        { kind: 'toggle', on: curtains, target: 'glass' },
        { kind: 'anim', clip: 'curtains', seconds: 0.6, target: 'glass' },
        { kind: 'monster', action: 'wave', seconds: 1 },
        { kind: 'particles', effect: curtains ? 'sparkle' : 'petals', count: 5 },
        { kind: 'sound', cue: 'swish_soft' },
        { kind: 'state', patch: { curtains, peeks } },
      ],
      cooldownMs: 240,
    }
  },
})

// ---------------------------------------------------------------------------
// ROOF — fits the walls beneath, shapes itself from its neighbours
// ---------------------------------------------------------------------------

/**
 * THE ROOF SURFACE, AND WHY IT IS NOT STEPS ANY MORE.
 *
 * The first version built each tile as three inset slabs. Every tile did the
 * right thing on its own and the finished roof was a Mayan pyramid: five hard
 * horizontal seams marching up to a flat cap. Law #5 says a kid cannot build
 * something ugly by accident, and that was the auto-snap system building the
 * ugly thing all by itself.
 *
 * A roof is now ONE CONTINUOUS SLOPED SURFACE per tile, sampled at nine points
 * — four corners, four edge middles and the centre — and each point is either
 * at EAVE height or at RIDGE height:
 *
 *   centre        always RIDGE
 *   edge middle   RIDGE if that side has a roof neighbour, else EAVE
 *   corner        RIDGE only if BOTH of its sides have roof neighbours
 *
 * That one rule is the whole hip-roof geometry:
 *   - a lone tile      -> every edge low, centre high  = a little pyramid
 *   - a straight run   -> N and S low, E and W high    = a ridge that continues
 *                                                        into its neighbours
 *   - the end of a run -> three sides low              = a hipped end
 *   - a 2x2 block      -> each tile high at the shared inner corner = a hip
 *   - a deep interior  -> all nine high                = the flat deck inside
 *
 * Neighbouring tiles agree by construction: any point on a shared edge is
 * decided by the same neighbour test from both sides, so the two surfaces meet
 * exactly, at every rotation, with no seam to get wrong.
 *
 * Cost: 2 draw calls per tile (fascia + cap), 1 for a fully interior tile.
 * The old stepped version cost up to 5. Geometry is cached per mask, so a
 * 30-tile roof allocates at most 16 buffers, not 30.
 */
const ROOF_EAVE = 0.14
const ROOF_RISE = 0.9
const ROOF_OVERHANG = 0.15

/**
 * The cap comes back as TWO meshes, and that split is what makes the shape
 * readable rather than a red plane: the sloping triangles keep the roof colour
 * and catch the light four different ways, and everything already up at ridge
 * height is a lighter deck tone that lines up exactly with the flat interior
 * tiles next to it. One mesh in a single colour looked flat from the isometric
 * camera even with a real pitch under it.
 */
interface RoofCap {
  slope: THREE.BufferGeometry | null
  deck: THREE.BufferGeometry | null
}

const roofCapGeos = new Map<string, RoofCap>()

function roofCapGeometry(t: number, lh: number, sides: Sides): RoofCap {
  const key = `${t}|${lh}|${sides.n ? 1 : 0}${sides.e ? 1 : 0}${sides.s ? 1 : 0}${sides.w ? 1 : 0}`
  const hit = roofCapGeos.get(key)
  if (hit !== undefined) return hit

  const over = t * ROOF_OVERHANG
  const x0 = sides.w ? -t / 2 : -t / 2 - over
  const x1 = sides.e ? t / 2 : t / 2 + over
  const z0 = sides.n ? -t / 2 : -t / 2 - over
  const z1 = sides.s ? t / 2 : t / 2 + over
  const xm = (x0 + x1) / 2
  const zm = (z0 + z1) / 2
  const lo = lh * ROOF_EAVE
  const hi = lo + lh * ROOF_RISE
  const h = (up: boolean): number => (up ? hi : lo)

  // Clockwise seen from above, starting at the NW corner. Consecutive pairs are
  // the perimeter edges the skirt hangs from.
  const ring: readonly (readonly [number, number, number])[] = [
    [x0, z0, h(sides.n && sides.w)],
    [xm, z0, h(sides.n)],
    [x1, z0, h(sides.n && sides.e)],
    [x1, zm, h(sides.e)],
    [x1, z1, h(sides.s && sides.e)],
    [xm, z1, h(sides.s)],
    [x0, z1, h(sides.s && sides.w)],
    [x0, zm, h(sides.w)],
  ]
  const centre: readonly [number, number, number] = [xm, zm, hi]

  const slopePos: number[] = []
  const deckPos: number[] = []
  const push = (into: number[], p: readonly [number, number, number], y?: number): void => {
    into.push(p[0], y ?? p[2], p[1])
  }

  for (let i = 0; i < 8; i++) {
    const a = ring[i] as readonly [number, number, number]
    const b = ring[(i + 1) % 8] as readonly [number, number, number]
    // A triangle with all three corners at ridge height is deck, not slope.
    const flat = a[2] > lo + 1e-5 && b[2] > lo + 1e-5
    const into = flat ? deckPos : slopePos
    // Reversed so the face normal points up.
    push(into, centre)
    push(into, b)
    push(into, a)
    // Skirt down to the eave, skipped where the whole edge is already there.
    if (a[2] <= lo + 1e-5 && b[2] <= lo + 1e-5) continue
    push(slopePos, a)
    push(slopePos, b)
    push(slopePos, b, lo)
    push(slopePos, a)
    push(slopePos, b, lo)
    push(slopePos, a, lo)
  }

  const bake = (pos: number[]): THREE.BufferGeometry | null => {
    if (pos.length === 0) return null
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3))
    geo.computeVertexNormals()
    return geo
  }
  const cap: RoofCap = { slope: bake(slopePos), deck: bake(deckPos) }
  roofCapGeos.set(key, cap)
  return cap
}

export const roofTile = definePlaceable<{ taps: number; bird: boolean }>({
  id: 'roof_tile' as PlaceableId,
  devName: 'Roof tile',
  category: 'roof',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  connect: { group: 'roof', style: 'edge' },
  tints: ['roofRed', 'roofTeal', 'roofPurple', 'roofMustard'],
  tags: ['starter', 'shell'],
  initialState: () => ({ taps: 0, bird: false }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const lh = ctx.grid.levelHeight
    const g = group('roof_tile')
    const col = ctx.tint ?? 'roofRed'
    const sides = sidesOf(ctx)
    const eaveH = lh * ROOF_EAVE
    const ridgeH = eaveH + lh * ROOF_RISE
    const tiles = group('tiles')

    // A tile with no open side is deep inside the roof: only its top face is
    // ever visible, so it is ONE flat box at ridge height — which is exactly
    // where its sloping neighbours arrive. Older-iPad budget.
    const deckCol = lighter(col, 0.12)
    if (sides.n && sides.e && sides.s && sides.w) {
      tiles.add(boxOn(t, ridgeH, t, { color: deckCol, name: 'deck' }))
      g.add(tiles)
      return g
    }

    // Fascia: overhangs only on the open sides, so a roof field stays flush.
    const over = t * ROOF_OVERHANG
    const ex0 = sides.w ? -t / 2 : -t / 2 - over
    const ex1 = sides.e ? t / 2 : t / 2 + over
    const ez0 = sides.n ? -t / 2 : -t / 2 - over
    const ez1 = sides.s ? t / 2 : t / 2 + over
    g.add(
      boxOn(ex1 - ex0, eaveH, ez1 - ez0, {
        color: darker(col, 0.28),
        x: (ex0 + ex1) / 2,
        z: (ez0 + ez1) / 2,
        name: 'eave',
      }),
    )

    // The slope itself: one continuous surface, shared cached geometry.
    const cap = roofCapGeometry(t, lh, sides)
    for (const [geo, colour, name] of [
      [cap.slope, col, 'slope'],
      [cap.deck, deckCol, 'deck'],
    ] as [THREE.BufferGeometry | null, PaletteName | number, string][]) {
      if (geo === null) continue
      const mesh = new THREE.Mesh(geo, toon(colour, { flat: true }))
      mesh.name = name
      mesh.castShadow = true
      mesh.receiveShadow = true
      tiles.add(mesh)
    }
    g.add(tiles)
    return g
  },

  // LAW #1: tap the roof and it drums; sometimes a bird lands on it.
  interact: (ctx) => {
    const taps = ctx.state.taps + 1
    const bird = taps % 3 === 0
    return {
      feedback: [
        { kind: 'anim', clip: bird ? 'bounce' : 'wobble', seconds: 0.4, target: 'tiles' },
        bird
          ? { kind: 'spawn', what: 'roofBird' }
          : { kind: 'particles', effect: 'dustPuff', count: 4 },
        { kind: 'sound', cue: bird ? 'chirp' : 'tap_tile' },
        { kind: 'state', patch: { taps, bird } },
      ],
      cooldownMs: 220,
    }
  },
})

// ---------------------------------------------------------------------------
// FLOORS — ground cover that tiles into one surface with a finished border
// ---------------------------------------------------------------------------

function floorArt(ctx: ArtContext, base: PaletteName, checker: boolean): THREE.Group {
  const t = ctx.grid.tileSize
  const g = group('floor')
  const sides = sidesOf(ctx)
  const alt = checker
    ? (ctx.anchor.gx + ctx.anchor.gz) % 2 === 0
      ? base
      : lighter(base, 0.22)
    : base
  g.add(boxOn(t, t * 0.09, t, { color: alt, name: 'slab' }))

  // Finished lip on every edge that is NOT touching another floor, so a laid
  // floor reads as one piece of joinery instead of a grid of squares.
  const rim = darker(base, 0.24)
  const lip: [boolean, number, number, number, number][] = [
    [!sides.n, t, t * 0.12, 0, -t * 0.44],
    [!sides.s, t, t * 0.12, 0, t * 0.44],
    [!sides.w, t * 0.12, t, -t * 0.44, 0],
    [!sides.e, t * 0.12, t, t * 0.44, 0],
  ]
  for (const [show, w, d, x, z] of lip) {
    if (!show) continue
    g.add(boxOn(w, t * 0.14, d, { color: rim, x, z, name: 'rim' }))
  }
  return g
}

export const floorWood = definePlaceable<{ steps: number }>({
  id: 'floor_wood' as PlaceableId,
  devName: 'Wooden floor',
  category: 'floor',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: true,
  connect: { group: 'floor', style: 'edge' },
  tints: ['floorWood', 'woodLight', 'wood', 'floorRug'],
  tags: ['starter', 'shell'],
  initialState: () => ({ steps: 0 }),
  buildArt: (ctx) => floorArt(ctx, ctx.tint ?? 'floorWood', false),
  interact: (ctx) => {
    const steps = ctx.state.steps + 1
    return {
      feedback: [
        { kind: 'monster', action: 'stomp', seconds: 0.6 },
        { kind: 'anim', clip: 'wobble', seconds: 0.3, target: 'slab' },
        { kind: 'particles', effect: 'dust', count: 3 },
        { kind: 'sound', cue: 'step_wood' },
        { kind: 'state', patch: { steps } },
      ],
      cooldownMs: 140,
    }
  },
})

export const floorTiles = definePlaceable<{ steps: number }>({
  id: 'floor_tile' as PlaceableId,
  devName: 'Checker floor',
  category: 'floor',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: true,
  connect: { group: 'floor', style: 'edge' },
  tints: ['floorTile', 'wallMint', 'wallBlue', 'wallPink'],
  tags: ['starter', 'shell'],
  initialState: () => ({ steps: 0 }),
  buildArt: (ctx) => floorArt(ctx, ctx.tint ?? 'floorTile', true),
  interact: (ctx) => {
    const steps = ctx.state.steps + 1
    return {
      feedback: [
        { kind: 'monster', action: 'hop', seconds: 0.6 },
        { kind: 'anim', clip: 'bounce', seconds: 0.3, target: 'slab' },
        { kind: 'particles', effect: 'sparkle', count: 4 },
        { kind: 'sound', cue: 'step_tile' },
        { kind: 'state', patch: { steps } },
      ],
      cooldownMs: 140,
    }
  },
})

// ---------------------------------------------------------------------------
// PATH — a second path that shares the `path` group with the starter one, so
// the two link into a single run without either knowing about the other.
// ---------------------------------------------------------------------------

export const pathBrick = definePlaceable<{ steps: number }>({
  id: 'path_brick' as PlaceableId,
  devName: 'Brick path',
  category: 'path',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: true,
  connect: { group: 'path', style: 'path' },
  // Warm earth first: a red-roof-coloured path reads as roofing from above and
  // a kid cannot tell the ground from the house. Learned from a capture.
  tints: ['dirt', 'sand', 'rock', 'roofRed'],
  tags: ['starter'],
  initialState: () => ({ steps: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('path_brick')
    const col = ctx.tint ?? 'dirt'
    // Full tile, flat: neighbouring tiles butt together with no seam and no lip
    // to trip the eye. The pattern on top does all the work.
    g.add(boxOn(t, t * 0.08, t, { color: darker(col, 0.28), name: 'slab' }))
    /*
     * A RUNNING BOND, not stripes that swing round with the connect mask.
     *
     * The pattern used to run lengthwise when a tile had only north/south
     * neighbours and crosswise otherwise, so a two-tile patio beside a path put
     * two tiles of PERPENDICULAR planks next to each other. Tinted brown, that
     * is exactly what the round-1 critic saw: "a brown door/deck piece lying
     * flat on the lawn like it fell over". Bricks are laid in a staggered bond
     * keyed off the tile's own coordinates instead, so the pattern is stable in
     * every direction, continues across neighbours, and always reads as paving.
     */
    const rows = 3
    const bw = t * 0.44
    for (let r = 0; r < rows; r++) {
      const z = (r - (rows - 1) / 2) * (t / rows)
      // Half-brick offset on alternate rows, carried across tiles by gz parity.
      const stagger = ((r + ctx.anchor.gz) % 2 === 0 ? 0 : bw / 2) - t * 0.22
      for (let c = -1; c <= 1; c++) {
        const x = c * bw + stagger
        if (Math.abs(x) > t * 0.5 + bw * 0.5) continue
        const w = Math.min(bw * 0.9, t * 0.5 - Math.abs(x) + bw * 0.45)
        if (w <= 0.02) continue
        g.add(
          boxOn(w, t * 0.03, t / rows - t * 0.045, {
            color: (r + c + ctx.anchor.gx) % 2 === 0 ? col : lighter(col, 0.12),
            x,
            y: t * 0.06,
            z,
            name: 'brick',
          }),
        )
      }
    }
    return g
  },

  interact: (ctx) => {
    const steps = ctx.state.steps + 1
    return {
      feedback: [
        { kind: 'monster', action: 'hop', seconds: 0.5 },
        { kind: 'particles', effect: 'dust', count: 4 },
        { kind: 'sound', cue: 'step_stone' },
        { kind: 'state', patch: { steps } },
      ],
      cooldownMs: 130,
    }
  },
})

// ---------------------------------------------------------------------------
// FENCE — posts and rails that link up the moment two are neighbours
// ---------------------------------------------------------------------------

export const fencePicket = definePlaceable<{ rattles: number }>({
  id: 'fence_picket' as PlaceableId,
  devName: 'Picket fence',
  category: 'fence',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  connect: { group: 'fence', style: 'fence' },
  tints: ['wallCream', 'woodLight', 'wallMint', 'wallPink'],
  tags: ['starter', 'yard'],
  initialState: () => ({ rattles: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('fence_picket')
    const col = ctx.tint ?? 'wallCream'
    const post = darker(col, 0.2)
    const sides = sidesOf(ctx)
    const H = t * 0.62

    const rail = (dx: number, dz: number, y: number): THREE.Mesh => {
      const alongX = dx !== 0
      return boxOn(alongX ? t * 0.56 : t * 0.1, t * 0.1, alongX ? t * 0.1 : t * 0.56, {
        color: col,
        x: dx * t * 0.26,
        y,
        z: dz * t * 0.26,
        name: 'rail',
      })
    }

    if (!sides.any) {
      // A lone fence still looks like a fence: two posts and two rails.
      g.add(boxOn(t * 0.14, H, t * 0.14, { color: post, x: -t * 0.4, name: 'post' }))
      g.add(boxOn(t * 0.14, H, t * 0.14, { color: post, x: t * 0.4, name: 'post' }))
      g.add(boxOn(t * 0.8, t * 0.1, t * 0.1, { color: col, y: t * 0.18, name: 'rail' }))
      g.add(boxOn(t * 0.8, t * 0.1, t * 0.1, { color: col, y: t * 0.4, name: 'rail' }))
      return g
    }

    // No ink outline on a linked post: a fence run is dozens of tiles and the
    // outline would double every one of them on the older-iPad budget. The
    // darker post colour carries the silhouette instead.
    g.add(boxOn(t * 0.16, H, t * 0.16, { color: post, name: 'post' }))
    for (const [on, dx, dz] of [
      [sides.n, 0, -1],
      [sides.e, 1, 0],
      [sides.s, 0, 1],
      [sides.w, -1, 0],
    ] as [boolean, number, number][]) {
      if (!on) continue
      g.add(rail(dx, dz, t * 0.18))
      g.add(rail(dx, dz, t * 0.4))
    }
    return g
  },

  // LAW #1: rattle the fence. It shakes and the pickets clatter.
  interact: (ctx) => {
    const rattles = ctx.state.rattles + 1
    return {
      feedback: [
        { kind: 'anim', clip: 'rattle', seconds: 0.45, target: 'rail' },
        { kind: 'monster', action: 'wiggle', seconds: 0.6 },
        { kind: 'particles', effect: 'dustPuff', count: 3 },
        { kind: 'sound', cue: 'fence_rattle' },
        { kind: 'state', patch: { rattles } },
      ],
      cooldownMs: 200,
    }
  },
})

// ---------------------------------------------------------------------------
// STEPS — how you get up onto anything you stacked
// ---------------------------------------------------------------------------

export const stairWood = definePlaceable<{ climbs: number }>({
  id: 'stair_wood' as PlaceableId,
  devName: 'Wooden steps',
  category: 'stair',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: true,
  walkable: true,
  tints: ['wood', 'woodLight', 'woodDark', 'rock'],
  tags: ['starter', 'shell'],
  initialState: () => ({ climbs: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const lh = ctx.grid.levelHeight
    const g = group('stair_wood')
    const col = ctx.tint ?? 'wood'
    const steps = 4
    for (let i = 0; i < steps; i++) {
      g.add(
        boxOn(t * 0.9, (lh * (i + 1)) / steps, t / steps, {
          color: i % 2 === 0 ? col : lighter(col, 0.1),
          z: t / 2 - (t / steps) * (i + 0.5),
          name: `tread${i}`,
        }),
      )
    }
    return g
  },

  interact: (ctx) => {
    const climbs = ctx.state.climbs + 1
    return {
      feedback: [
        { kind: 'monster', action: 'climb', seconds: 1.1 },
        { kind: 'anim', clip: 'bounce', seconds: 0.4, target: 'tread0' },
        { kind: 'particles', effect: 'dust', count: 3 },
        { kind: 'sound', cue: 'step_wood' },
        { kind: 'state', patch: { climbs } },
      ],
      cooldownMs: 240,
    }
  },
})

// ---------------------------------------------------------------------------
// R3 DATA DROP — "there is nothing here" was a content problem
// ---------------------------------------------------------------------------
//
// Round-3 critic, minor: "Four of the nine categories hold a single piece, which
// renders as one lonely tile floating in a full-width tray and reads as 'there
// is nothing here'." The drawer now folds a one-piece tab into a related one
// (see MIN_TAB_TILES in src/build/palette.ts) — that is the structural answer,
// and it heals itself as crews land content. This is the other half: three more
// day-one pieces so the thinnest tabs have a real CHOICE in them, which is what
// a tab is for. All free forever, all `interact`, no engine change.

/** A stone block. FREE FOREVER. The heavy sibling of the wooden one. */
export const blockStone = definePlaceable<{ knocks: number }>({
  id: 'block_stone' as PlaceableId,
  devName: 'Stone block',
  category: 'block',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: true,
  walkable: false,
  tints: ['rock', 'rockDark', 'wallCream', 'wallLilac'],
  tags: ['starter'],
  initialState: () => ({ knocks: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('block_stone')
    const col = ctx.tint ?? 'rock'
    g.add(inked(boxOn(t * 0.92, t * 0.86, t * 0.92, { color: col, name: 'body' })))
    // Two chunky courses so a stack of them reads as stonework, not one slab.
    g.add(boxOn(t * 0.96, t * 0.08, t * 0.96, { color: darker(col, 0.25), y: t * 0.4, name: 'course' }))
    g.add(boxOn(t * 0.4, t * 0.22, t * 0.06, { color: lighter(col, 0.2), y: t * 0.52, z: t * 0.47, name: 'chip' }))
    return g
  },

  interact: (ctx) => {
    const knocks = ctx.state.knocks + 1
    const lucky = knocks % 5 === 0
    return {
      feedback: [
        { kind: 'anim', clip: lucky ? 'bounce' : 'wobble', seconds: 0.35, target: 'body' },
        { kind: 'particles', effect: lucky ? 'stars' : 'dustPuff', count: lucky ? 8 : 4 },
        { kind: 'sound', cue: lucky ? 'chime_happy' : 'knock_wood' },
        { kind: 'state', patch: { knocks } },
      ],
      cooldownMs: 250,
    }
  },
})

/** A hedge. FREE FOREVER. Shares the `fence` group, so it links to pickets. */
export const fenceHedge = definePlaceable<{ rustles: number }>({
  id: 'fence_hedge' as PlaceableId,
  devName: 'Hedge',
  category: 'fence',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  connect: { group: 'fence', style: 'fence' },
  tints: ['leaf', 'leafDark', 'leafLight'],
  tags: ['starter', 'yard', 'cosy'],
  initialState: () => ({ rustles: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('fence_hedge')
    const col = ctx.tint ?? 'leaf'
    const sides = sidesOf(ctx)
    const H = t * 0.66
    g.add(boxOn(t * 0.72, H, t * 0.72, { color: col, name: 'bush' }))
    g.add(boxOn(t * 0.5, t * 0.16, t * 0.5, { color: lighter(col, 0.22), y: H - t * 0.06, name: 'top' }))
    // Grow toward every neighbour so a run of hedge is one unbroken green wall.
    for (const [on, dx, dz] of [
      [sides.n, 0, -1],
      [sides.e, 1, 0],
      [sides.s, 0, 1],
      [sides.w, -1, 0],
    ] as [boolean, number, number][]) {
      if (!on) continue
      const alongX = dx !== 0
      g.add(
        boxOn(alongX ? t * 0.36 : t * 0.72, H * 0.92, alongX ? t * 0.72 : t * 0.36, {
          color: col,
          x: dx * t * 0.34,
          z: dz * t * 0.34,
          name: 'link',
        }),
      )
    }
    return g
  },

  interact: (ctx) => {
    const rustles = ctx.state.rustles + 1
    return {
      feedback: [
        { kind: 'anim', clip: 'rattle', seconds: 0.5, target: 'top' },
        { kind: 'monster', action: 'sniff', seconds: 0.9 },
        { kind: 'particles', effect: 'petals', count: 6 },
        { kind: 'sound', cue: 'fence_rattle' },
        { kind: 'state', patch: { rustles } },
      ],
      cooldownMs: 200,
    }
  },
})

/** A lawn tile. FREE FOREVER. Shares the `floor` group with the indoor floors. */
export const floorGrass = definePlaceable<{ steps: number }>({
  id: 'floor_grass' as PlaceableId,
  devName: 'Lawn',
  category: 'floor',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: true,
  connect: { group: 'floor', style: 'edge' },
  tints: ['grass', 'grassLight', 'grassDark', 'leafLight'],
  tags: ['starter', 'yard', 'cosy'],
  initialState: () => ({ steps: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('floor_grass')
    const col = ctx.tint ?? 'grass'
    g.add(boxOn(t, t * 0.1, t, { color: col, name: 'slab' }))
    // A few blades, placed from `variety` so no two tiles are identical and the
    // lawn never reads as a repeated stamp.
    const n = 3
    for (let i = 0; i < n; i++) {
      const a = (ctx.variety + i / n) * Math.PI * 2
      g.add(
        boxOn(t * 0.08, t * 0.16, t * 0.08, {
          color: lighter(col, 0.18),
          x: Math.cos(a) * t * 0.26,
          y: t * 0.1,
          z: Math.sin(a) * t * 0.26,
          name: 'blade',
        }),
      )
    }
    return g
  },

  interact: (ctx) => {
    const steps = ctx.state.steps + 1
    return {
      feedback: [
        { kind: 'monster', action: 'hop', seconds: 0.6 },
        { kind: 'anim', clip: 'wobble', seconds: 0.4, target: 'blade' },
        { kind: 'particles', effect: 'petals', count: 4 },
        { kind: 'sound', cue: 'step_soft' },
        { kind: 'state', patch: { steps } },
      ],
      cooldownMs: 140,
    }
  },
})

/** Everything in this drop. Spread into ALL_CONTENT in src/content/index.ts. */
export const BUILD_KIT = [
  wallPlaster,
  doorWood,
  windowSquare,
  roofTile,
  floorWood,
  floorTiles,
  floorGrass,
  pathBrick,
  fencePicket,
  fenceHedge,
  stairWood,
  blockStone,
]
